import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, RefreshCw, LineChart, Trash2, Calculator } from 'lucide-react';
import { toast } from 'sonner';

interface FundingRoundCardProps {
  companyId: string;
}

interface FundingRound {
  id: string;
  name: string;
  round_type: string;
  instrument: string;
  target_amount: number | null;
  currency: string;
  pre_money: number | null;
  status: string;
  created_at: string;
  is_applied: boolean;
}

export function FundingRoundCard({ companyId }: FundingRoundCardProps) {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState<FundingRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    round_type: 'SEED',
    target_amount: '',
    currency: 'USD',
    pre_money: '',
    option_pool_increase: '',
    instrument: 'EQUITY',
  });

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from('funding_rounds')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error(error.message);
    } else {
      setRounds(data || []);
    }
    setLoading(false);
  }

  async function save() {
    if (!form.name || !form.target_amount) {
      toast.error('Enter round name and target');
      return;
    }

    setSaving(true);
    const { error } = await supabase.functions.invoke('create-funding-round', {
      body: { companyId, params: form },
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Round saved');
      setForm({
        name: '',
        round_type: 'SEED',
        target_amount: '',
        currency: 'USD',
        pre_money: '',
        option_pool_increase: '',
        instrument: 'EQUITY',
      });
      load();
    }
    setSaving(false);
  }

  async function deleteDraft(roundId: string, roundName: string) {
    if (!confirm(`Delete draft round "${roundName}"?`)) return;

    const { error } = await supabase
      .from('funding_rounds')
      .delete()
      .eq('id', roundId)
      .eq('status', 'DRAFT');

    if (error) {
      toast.error('Failed to delete: ' + error.message);
    } else {
      toast.success('Draft deleted');
      load();
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Funding Rounds</CardTitle>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Create / Edit */}
        <div className="grid sm:grid-cols-3 gap-2">
          <Input
            placeholder="Round Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <Select
            value={form.round_type}
            onValueChange={(v) => setForm({ ...form, round_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PRE-SEED">Pre-Seed</SelectItem>
              <SelectItem value="SEED">Seed</SelectItem>
              <SelectItem value="SAFE">SAFE</SelectItem>
              <SelectItem value="SERIES_A">Series A</SelectItem>
              <SelectItem value="SERIES_B">Series B</SelectItem>
              <SelectItem value="SERIES_C">Series C</SelectItem>
              <SelectItem value="BRIDGE">Bridge</SelectItem>
              <SelectItem value="SECONDARY">Secondary</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={form.instrument}
            onValueChange={(v) => setForm({ ...form, instrument: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EQUITY">Equity</SelectItem>
              <SelectItem value="SAFE">SAFE</SelectItem>
              <SelectItem value="CONVERTIBLE">Convertible</SelectItem>
              <SelectItem value="OTHER">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-3 gap-2">
          <Input
            type="number"
            placeholder="Target Amount"
            value={form.target_amount}
            onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
            className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
          />
          <Input
            type="number"
            placeholder="Pre-Money Valuation"
            value={form.pre_money}
            onChange={(e) => setForm({ ...form, pre_money: e.target.value })}
            className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
          />
          <Input
            type="number"
            placeholder="Option Pool %"
            value={form.option_pool_increase}
            onChange={(e) => setForm({ ...form, option_pool_increase: e.target.value })}
            className="[&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
          />
        </div>

        <div className="flex gap-2">
          <Input
            className="w-32"
            placeholder="Currency"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
          />
          <Button onClick={save} disabled={saving}>
            <Save className="h-4 w-4 mr-1" />
            Save
          </Button>
        </div>

        {/* Rounds Table */}
        {rounds.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Instrument</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Pre-Money</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rounds.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.round_type}</TableCell>
                  <TableCell>{r.instrument}</TableCell>
                  <TableCell>
                    {r.currency} {r.target_amount ? Number(r.target_amount).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {r.currency} {r.pre_money ? Number(r.pre_money).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {/* The only route from a round set up here to the share
                          register. Without it, a round could be created, sit in
                          this table, and never reach the cap table — which is
                          exactly what happened before. */}
                      {!r.is_applied && (
                        <Button
                          size="sm"
                          onClick={() => navigate(`/funding/scenarios?round=${r.id}`)}
                        >
                          <Calculator className="h-4 w-4 mr-1" />
                          Price this round
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/funding/valuation?roundId=${r.id}`)}
                      >
                        <LineChart className="h-4 w-4 mr-1" />
                        Run Valuation
                      </Button>
                      {r.status === 'DRAFT' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteDraft(r.id, r.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">No rounds yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
